// Lightweight in-app perf instrumentation, gated on a `perf=1` URL
// query param so it's strictly zero overhead in prod. The Playwright
// harness at `perf/harness.ts` navigates with the flag, drives a
// scripted scenario, then reads `window.__perf.dump()` to capture
// per-span aggregates (count, mean, p50, p95, p99, max, total).
//
// Hot-path wrapping idiom:
//   perfSpan("renderConnections", () => { ... })
//
// When the flag is off, `perfSpan` is the identity function (just runs
// the closure) — no allocation, no Map lookup. When on, each call
// records performance.now() before/after into a per-name array. The
// arrays are kept in-memory; dump() reduces them to summary stats.

interface SpanSummary {
  count: number;
  mean: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
  total: number;
}

const spans = new Map<string, number[]>();

function readEnabled(): boolean {
  if (typeof window === "undefined") return false;
  // Accept ?perf=1 in either the URL search or the hash query (the
  // router uses hash-routing, so the user's typical URL has the params
  // in the hash). The harness chooses the search form for simplicity.
  const search = window.location.search;
  if (search.includes("perf=1")) return true;
  const hash = window.location.hash;
  const q = hash.indexOf("?");
  if (q >= 0 && hash.slice(q + 1).includes("perf=1")) return true;
  return false;
}

const enabled = readEnabled();

export function perfEnabled(): boolean {
  return enabled;
}

export function perfSpan<T>(name: string, fn: () => T): T {
  if (!enabled) return fn();
  const start = performance.now();
  try {
    return fn();
  } finally {
    const dur = performance.now() - start;
    let arr = spans.get(name);
    if (!arr) {
      arr = [];
      spans.set(name, arr);
    }
    arr.push(dur);
  }
}

// Manual mark for sites where wrapping a closure is awkward (e.g. a
// callback fired by vis-timeline that returns void but spans real
// work). Call `perfMark.start("foo")` and `perfMark.end("foo")` —
// nesting / overlap is the caller's responsibility.
const pending = new Map<string, number>();
export const perfMark = {
  start(name: string): void {
    if (!enabled) return;
    pending.set(name, performance.now());
  },
  end(name: string): void {
    if (!enabled) return;
    const start = pending.get(name);
    if (start === undefined) return;
    pending.delete(name);
    const dur = performance.now() - start;
    let arr = spans.get(name);
    if (!arr) {
      arr = [];
      spans.set(name, arr);
    }
    arr.push(dur);
  },
};

function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * q));
  return sorted[idx]!;
}

function summary(): Record<string, SpanSummary> {
  const out: Record<string, SpanSummary> = {};
  const names = [...spans.keys()].sort();
  for (const name of names) {
    const arr = spans.get(name)!;
    if (arr.length === 0) continue;
    const sorted = [...arr].sort((a, b) => a - b);
    const sum = sorted.reduce((acc, n) => acc + n, 0);
    out[name] = {
      count: sorted.length,
      mean: sum / sorted.length,
      p50: quantile(sorted, 0.5),
      p95: quantile(sorted, 0.95),
      p99: quantile(sorted, 0.99),
      max: sorted[sorted.length - 1]!,
      total: sum,
    };
  }
  return out;
}

declare global {
  interface Window {
    __perf?: {
      dump: () => Record<string, SpanSummary>;
      clear: () => void;
      enabled: boolean;
    };
  }
}

if (enabled && typeof window !== "undefined") {
  window.__perf = {
    dump: summary,
    clear: () => {
      spans.clear();
      pending.clear();
    },
    enabled: true,
  };
}
